import { useState } from 'react';
import type { ProgramPublicProfile } from '@/mocks/programProfile';
import type { AdminPost } from '@/mocks/adminPosts';

interface Props {
  profile: ProgramPublicProfile;
}

export default function ProgramFeedSection({ profile }: Props) {
  const [likedPosts, setLikedPosts] = useState<Set<number>>(new Set());

  const toggleLike = (postId: number) => {
    setLikedPosts((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  if (profile.posts.length === 0) {
    return (
      <section className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
            <i className="ri-article-line text-accent-600 text-sm"></i>
          </div>
          <h2 className="text-base font-bold text-foreground-950">Publicaciones</h2>
        </div>
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-article-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-500">Sin publicaciones</p>
          <p className="text-xs text-foreground-400 mt-1">Pronto habrá contenido para este programa</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
          <i className="ri-article-line text-accent-600 text-sm"></i>
        </div>
        <h2 className="text-base font-bold text-foreground-950">Publicaciones</h2>
        <span className="text-xs text-foreground-400 ml-auto">{profile.posts.length} publicaciones</span>
      </div>

      <div className="space-y-4">
        {profile.posts.map((post) => (
          <PostCard key={post.id} post={post} isLiked={likedPosts.has(post.id)} onToggleLike={() => toggleLike(post.id)} />
        ))}
      </div>
    </section>
  );
}

function PostCard({ post, isLiked, onToggleLike }: { post: AdminPost; isLiked: boolean; onToggleLike: () => void }) {
  return (
    <div className="bg-background-100/70 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Image */}
      {post.imageUrl && post.type !== 'encuesta' && (
        <div className="w-full h-52 overflow-hidden">
          <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="p-4 md:p-5">
        {/* Meta */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
            post.type === 'encuesta'
              ? 'bg-amber-100 text-amber-700'
              : post.type === 'imagen'
                ? 'bg-accent-100 text-accent-700'
                : 'bg-secondary-100 text-secondary-700'
          }`}>
            {post.type === 'encuesta' ? 'Encuesta' : post.type === 'imagen' ? 'Imagen' : post.type === 'video' ? 'Video' : 'Texto'}
          </span>
          <span className="text-[10px] text-foreground-400">
            {new Date(post.createdAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          {post.isFeatured && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-white text-[10px] font-bold">Destacado</span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-sm font-bold text-foreground-900 mb-1.5">{post.title}</h3>

        {/* Text preview */}
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-3 mb-3">
          {post.text}
        </p>

        {/* Poll */}
        {post.type === 'encuesta' && post.pollData && (
          <div className="space-y-2 mb-3">
            {post.pollData.options.map((opt) => {
              const total = post.pollData!.options.reduce((s, o) => s + o.votes, 0);
              const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
              return (
                <div key={opt.id} className="relative">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-foreground-700">{opt.text}</span>
                    <span className="text-[10px] font-semibold text-foreground-500">{pct}%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500 transition-all" style={{ width: `${pct}%` }}></div>
                  </div>
                </div>
              );
            })}
            <p className="text-[10px] text-foreground-400 mt-1">
              {post.pollData.options.reduce((s, o) => s + o.votes, 0).toLocaleString()} votos
            </p>
          </div>
        )}

        {/* Engagement */}
        <div className="flex items-center gap-4 pt-2 border-t border-background-200/70">
          <button
            onClick={onToggleLike}
            className={`flex items-center gap-1 text-xs transition-colors cursor-pointer ${isLiked ? 'text-accent-600 font-semibold' : 'text-foreground-400 hover:text-foreground-600'}`}
          >
            <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
            {isLiked ? post.engagement.likes + 1 : post.engagement.likes}
          </button>
          <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
            <i className="ri-chat-1-line text-sm"></i>
            {post.engagement.comments}
          </button>
          <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
            <i className="ri-share-forward-line text-sm"></i>
            {post.engagement.shares}
          </button>
        </div>
      </div>
    </div>
  );
}